import { Button, Grid, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { SecondaryTitle } from 'src/shared/components/atoms/secondaryTitle';
import { ArrowForwardIos } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useMemo } from 'react';
import { formatPercent } from 'src/shared/helpers/formatPercents';
import { chartHelper } from 'src/shared/helpers/chartHelper';
import { useStatisticsContext } from 'src/shared/contexts/statisticsContext';
import { formatAmount } from 'src/shared/functions/formatAmount';
import { AssociationAndDistrictSelect } from 'src/shared/components/organisms/associationAndDistrictSelect';
import { useGlobals } from 'src/shared/contexts/appContext';
import { StatisticsChartLine } from './statisticsChartLine';
import { StatisticsContentHeader } from './statisticsContentHeader';
import { StatisticsFilterAutocompleteDatePicker } from './statisticsFilterAutocompleteDatePicker';
import { StatisticsMemberGrowthContent } from './statisticsMemberGrowthContent';
import { useMemberGrowth } from '../hooks/useFetchMemberGrowth';

export const StatisticsMemberGrowthOverview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useGlobals();
  const { searchFilter, association, setAssociation, district, setDistrict } = useStatisticsContext();
  const { memberGrowth, isLoading } = useMemberGrowth(searchFilter);

  const navigateHandler = () => {
    navigate(RoutesEnum.StatisticsMemberProgress);
  };

  const chartData = useMemo(() => memberGrowth && chartHelper.transformData(memberGrowth.memberDiffItems), [memberGrowth]);

  return (
    <Paper sx={{ flexGrow: 1 }}>
      <SecondaryTitle title={t('statistics:memberProgress.title')} divider uppercase />
      <PageFilterSection
        leftContent={
          <Grid container spacing={2}>
            <StatisticsFilterAutocompleteDatePicker />

            {currentUser && (
              <AssociationAndDistrictSelect
                hideDistrict={currentUser?.districts.length === 0}
                association={association}
                district={district}
                useGridLayout
                associationCallback={(val) => setAssociation(val || undefined)}
                districtCallback={(val) => setDistrict(val || undefined)}
                gridXs={12}
                gridLg={3}
              />
            )}
          </Grid>
        }
      />

      <Grid container p={4} pb={2}>
        <Grid item xs={12} lg={8}>
          <Grid item xs={12}>
            <StatisticsContentHeader
              isLoading={isLoading}
              title={t('statistics:memberProgress.sideTitle')}
              subtitle={
                memberGrowth && memberGrowth.differenceCount > 0
                  ? `+${formatAmount(memberGrowth?.differenceCount)}`
                  : `${formatAmount(memberGrowth?.differenceCount ?? 0)}`
              }
              endEndornment='st'
              badge={memberGrowth && formatPercent(memberGrowth?.differencePercent)}
            />
          </Grid>
          <Grid item xs={12}>
            <StatisticsChartLine chartData={chartData} isLoading={isLoading} />
          </Grid>
        </Grid>

        <Grid item xs={12} lg={4}>
          <StatisticsMemberGrowthContent isLoading={isLoading} growthData={memberGrowth} />
        </Grid>

        <Grid item xs={12} mt={1}>
          <Button variant='text' size='small' onClick={navigateHandler} endIcon={<ArrowForwardIos fontSize='inherit' />}>
            {t('common:button.showMore')}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};
